import { useEffect, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Avatar,
  Button,
  Empty as AntdEmpty,
  Input,
  Spin,
  Typography,
  Select,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useDebounce } from 'usehooks-ts';
import {
  SortOption,
  SORT_OPTIONS,
  useSortedProcs,
} from '@/entities/proc/sorting';
import { api } from '@/shared/api';
import { PageRoutes } from '@/shared/router';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { Row } from '@/shared/ui';
import { intialsFromName } from '@/shared/utils';
import { PageLayout } from '@/widgets/page-layout';
import { ProcCard } from '@/widgets/proc-card';
import EmptyIcon from '@assets/empty.svg';

const PROCS_PER_PAGE = 50;
const { Title } = Typography;

const HeaderContent = () => {
  const navigate = useNavigate();

  return (
    <HeaderWrapper>
      <Row>
        <Logo
          src="https://suresteps-public.s3.amazonaws.com/wordmark.svg"
          onClick={() => navigate('/')}
          style={{ height: 25 }}
        />
      </Row>
      <MenuButtons>
        <Button onClick={() => navigate('/login')}>Login</Button>
        <Button type="primary" onClick={() => navigate(PageRoutes.SIGNUP)}>
          Sign up
        </Button>
      </MenuButtons>
    </HeaderWrapper>
  );
};

export const Discover = () => {
  const { theme } = useThemeContext();
  const params = useParams();
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>(
    SortOption.ALPHABETICAL,
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [organization, setOrganization] = useState<{
    name: string;
    image: string;
  }>();

  const debouncedValue = useDebounce<string>(searchTerm, 350);

  async function fetchProcs({ pageParam = 1 }) {
    return await api.get(`/public/search`, {
      params: {
        page: pageParam,
        take: PROCS_PER_PAGE,
        query: debouncedValue,
        organizationId: params?.organizationId,
      },
    });
  }

  const {
    data: procsData,
    isLoading: procsDataLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'discoverPublicProcsGetPaginated',
      debouncedValue,
      params?.organizationId,
    ],
    enabled: !(
      !params?.organizationId && location.pathname.includes('/organization')
    ),
    queryFn: fetchProcs,
    keepPreviousData: true,
    getNextPageParam: ({ data }) => {
      if (data.meta.hasNextPage) {
        return data.meta.page + 1;
      }
      return false;
    },
  });

  useEffect(() => {
    if (!params?.organizationId) {
      setOrganization(undefined);
      return;
    }
    if (procsData?.pages[0]?.data?.data[0]) {
      setOrganization({
        name: procsData?.pages[0]?.data?.data[0]?.organizationName,
        image:
          procsData?.pages[0]?.data?.data[0]?.organizationUploadedImageLink,
      });
    }
  }, [params?.organizationId, procsData]);

  const sortedProcs = useSortedProcs(procsData, selectedSortOption);

  return (
    <PageLayout
      headerContent={<HeaderContent />}
      contentStyle={{
        background: themeConfig[theme].colors.bgBase,
        padding: 0,
      }}
    >
      {organization && (
        <Banner>
          <Row gap={30} style={{ marginBottom: 30 }}>
            <ImageWrapper
              style={{
                border: `4px solid ${themeConfig[theme].colors.layoutBg}`,
              }}
            >
              <Avatar
                icon={
                  <Initials
                    style={{
                      color: themeConfig[theme].colors.text,
                    }}
                  >
                    {intialsFromName(organization?.name)}
                  </Initials>
                }
                src={organization?.image}
                style={{
                  backgroundColor: themeConfig[theme].colors.bgBase,
                  color: '#000',
                  border: `1px solid ${themeConfig[theme].colors.text}`,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            </ImageWrapper>
            <Title style={{ marginTop: -20 }}>{organization.name}</Title>
          </Row>
        </Banner>
      )}
      <ContentWrapper>
        <Row justifyContent="space-between" style={{ marginBottom: 30 }}>
          <Input
            placeholder="Search procs"
            size="large"
            allowClear
            suffix={<SearchOutlined />}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '70%' }}
          />
          <Typography.Text style={{ marginRight: 8, paddingLeft: 150 }}>
            Sort by:
          </Typography.Text>
          <Select
            defaultValue={SortOption.ALPHABETICAL}
            options={SORT_OPTIONS}
            style={{ width: 200 }}
            onChange={(value) => setSelectedSortOption(value as SortOption)}
          />
        </Row>
        {procsDataLoading ? (
          <Spin />
        ) : sortedProcs.length === 0 ? (
          <Empty image={EmptyIcon} description="There are no procs to show" />
        ) : (
          <>
            <Grid>
              {sortedProcs.map((item) => (
                <ProcCard key={item.id} proc={item} type="discover" />
              ))}
            </Grid>
            {hasNextPage && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  margin: '50px 0 20px',
                }}
              >
                <Button type="primary" onClick={() => fetchNextPage()}>
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </ContentWrapper>
    </PageLayout>
  );
};

const HeaderWrapper = styled(Row)`
  width: 100%;
  justify-content: space-between;
`;

const Logo = styled.img`
  :hover {
    cursor: pointer;
  }
`;

const MenuButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  grid-gap: 20px;
  align-items: stretch;
  padding-bottom: 50px;
`;

const Banner = styled.div`
  height: 20%;
  background: linear-gradient(224deg, #8117a7 0%, #1d6ab9 49.48%, #82e580 100%);
`;

const ContentWrapper = styled.div`
  padding: 25px;
  padding-bottom: 40px;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
`;

const Empty = styled(AntdEmpty)`
  margin-inline: 0;
`;

const Initials = styled.div`
  font-size: 52px;
  font-weight: bold;
  text-transform: uppercase;
`;
